export const parseCertificateObject = value => {
  if (!value) return {};
  if (typeof value === 'object' && !Array.isArray(value)) return value;
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
};

export const certificatePathValue = (source, path) => String(path || '').split('.').reduce(
  (value, key) => (value === undefined || value === null ? undefined : value[key]),
  source,
);

export const toMarathiDigits = value => String(value ?? '').replace(/[0-9]/g, digit => '०१२३४५६७८९'[Number(digit)]);

const dateParts = value => {
  if (!value) return null;
  const input = String(value);
  const parsed = /^\d{4}-\d{2}-\d{2}$/.test(input)
    ? new Date(`${input}T00:00:00Z`)
    : new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return {
    day: String(parsed.getUTCDate()).padStart(2, '0'),
    month: String(parsed.getUTCMonth() + 1).padStart(2, '0'),
    year: String(parsed.getUTCFullYear()),
  };
};

export const formatCertificateGender = value => {
  const normalized = String(value || '').trim().toLocaleLowerCase('en-IN');
  if (['male', 'm', 'पुरुष', 'पुरूष'].includes(normalized)) return 'पुरुष / Male';
  if (['female', 'f', 'स्त्री', 'महिला'].includes(normalized)) return 'स्त्री / Female';
  if (['other', 'इतर', 'अन्य'].includes(normalized)) return 'इतर / Other';
  return normalized ? String(value).trim() : '';
};

export const maskCertificateValue = value => {
  if (value === undefined || value === null) return '';
  const str = String(value).trim();
  if (!str) return '';
  const alnumMatches = str.match(/[a-zA-Z0-9\u0966-\u096F]/g);
  if (!alnumMatches || alnumMatches.length <= 4) {
    return str;
  }
  const chars = str.split('');
  let keepCount = 0;
  for (let i = chars.length - 1; i >= 0; i--) {
    if (/[a-zA-Z0-9\u0966-\u096F]/.test(chars[i])) {
      keepCount++;
      if (keepCount > 4) {
        chars[i] = 'X';
      }
    }
  }
  return chars.join('');
};

export const formatCertificateValue = (value, formatter = 'text') => {
  if (value === undefined || value === null || (typeof value === 'string' && !value.trim())) return '';
  const parts = dateParts(value);
  switch (formatter) {
    case 'uppercase': return String(value).toLocaleUpperCase('en-IN');
    case 'masked': return maskCertificateValue(value);
    case 'date': return parts ? `${toMarathiDigits(`${parts.day}/${parts.month}/${parts.year}`)} / ${parts.day}/${parts.month}/${parts.year}` : String(value);
    case 'date_mr': return parts ? toMarathiDigits(`${parts.day}/${parts.month}/${parts.year}`) : String(value);
    case 'date_en': return parts ? `${parts.day}/${parts.month}/${parts.year}` : String(value);
    case 'number': return Number.isFinite(Number(value)) ? new Intl.NumberFormat('en-IN').format(Number(value)) : String(value);
    case 'marathi_digits': return toMarathiDigits(value);
    case 'currency': return Number.isFinite(Number(value)) ? `₹ ${new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(Number(value))}` : String(value);
    case 'percentage': return Number.isFinite(Number(value)) ? `${new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(Number(value))}%` : String(value);
    case 'gender': return formatCertificateGender(value);
    default: return String(value);
  }
};

const TOKEN_PATTERN = /{{\s*([a-z][a-z0-9_.]*)(?:\|([a-z_]+))?\s*}}/gi;

export const renderCertificateText = (text, context, placeholder = '—') => String(text || '').replace(
  TOKEN_PATTERN,
  (_token, path, formatter = 'text') => formatCertificateValue(certificatePathValue(context, path), formatter) || placeholder,
);

export const CERTIFICATE_LAYOUT_DEFAULTS = Object.freeze({
  page: {
    size: 'A4', orientation: 'portrait', accent_color: '#1a237e', background_color: '#ffffff',
    text_color: '#111827', border_color: '#1a237e', border_width: 2, horizontal_padding: 40,
  },
  header: {
    visible: true, alignment: 'center', background_color: '#ffffff', text_color: '#0f172a',
    title_color: '#1a237e', show_emblem: true, emblem_source: 'ashoka', emblem_url: '/certificate-assets/ashoka-emblem.png',
    show_government_label: true, show_authority_reference: true, show_form_label: true,
    show_title: true, show_legal_reference: true, show_certificate_number: true, content_blocks: [],
  },
  body: {
    background_color: '#ffffff', text_color: '#172033', secondary_text_color: '#64748b',
    variable_color: '#1a237e', field_background_color: '#ffffff', field_border_color: '#d7dee8',
    show_field_borders: false, layout_mode: 'flow', canvas_height: 520, alignment: 'left', block_gap: 9,
  },
  footer: {
    visible: true, background_color: '#ffffff', text_color: '#64748b', accent_color: '#1a237e',
    show_place_date: true, show_qr: true, show_left_signature: true, show_right_signature: true,
    show_verification: true, content_blocks: [],
  },
});

const SAMPLE_PHOTO_DATA_URL = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(`
  <svg xmlns="http://www.w3.org/2000/svg" width="240" height="300" viewBox="0 0 240 300">
    <rect width="240" height="300" fill="#e2e8f0"/>
    <circle cx="120" cy="100" r="48" fill="#94a3b8"/>
    <path d="M42 280c8-70 42-108 78-108s70 38 78 108" fill="#94a3b8"/>
    <text x="120" y="288" text-anchor="middle" font-family="Arial" font-size="15" fill="#334155">SAMPLE PHOTO</text>
  </svg>
`)}`;

export const certificateSchema = templateSnapshot => {
  const parsed = parseCertificateObject(templateSnapshot);
  const source = parsed?.schema || parsed;
  const accent = source?.page?.accent_color || CERTIFICATE_LAYOUT_DEFAULTS.page.accent_color;
  const legacyCustomEmblem = source?.header?.emblem_source === 'custom'
    && /^\/uploads\/(?!certificate-assets\/)[a-z0-9][a-z0-9_./-]*\.(?:png|jpe?g)$/i.test(String(source?.header?.emblem_url || ''))
    && !String(source.header.emblem_url).includes('..')
    && !String(source.header.emblem_url).includes('//')
    && !String(source.header.emblem_url).includes('\\');
  const configuredAssetPolicy = Number(source?.asset_policy_version);
  return {
    ...source,
    asset_policy_version: [1, 2].includes(configuredAssetPolicy)
      ? configuredAssetPolicy
      : legacyCustomEmblem ? 1 : 2,
    page: {
      ...CERTIFICATE_LAYOUT_DEFAULTS.page,
      border_color: accent,
      ...(source?.page || {}),
    },
    header: {
      ...CERTIFICATE_LAYOUT_DEFAULTS.header,
      title_color: accent,
      ...(source?.header || {}),
      content_blocks: Array.isArray(source?.header?.content_blocks) ? source.header.content_blocks : [],
    },
    body: {
      ...CERTIFICATE_LAYOUT_DEFAULTS.body,
      variable_color: accent,
      ...(source?.body || {}),
    },
    blocks: Array.isArray(source?.blocks) ? source.blocks : [],
    signature: { ...(source?.signature || {}) },
    footer: {
      ...CERTIFICATE_LAYOUT_DEFAULTS.footer,
      accent_color: accent,
      ...(source?.footer || {}),
      content_blocks: Array.isArray(source?.footer?.content_blocks) ? source.footer.content_blocks : [],
    },
  };
};

export const buildCertificatePreviewContext = ({ variables = [], serviceType = 'custom' } = {}) => {
  const context = {
    certificate: {
      number: 'CERT-001-000001', application_uid: 'AP-GP001-20260803-0001', type: serviceType,
      issued_at: '2026-08-03', registration_date: '2026-08-03', register_volume: '2026',
      register_serial: '1', revision: 1, verification_url: 'https://example.invalid/verify',
      verification_hash: 'A1B2C3D4E5F60708',
    },
    tenant: {
      name: 'नमुना ग्रामपंचायत', slug: 'sample', address: 'ग्रामपंचायत कार्यालय', taluka: 'तालुका',
      district: 'जिल्हा', state: 'महाराष्ट्र', pincode: '४१२३०३', contact_phone: '०२०-१२३४५६७८',
      contact_email: 'office@example.in', logo_url: '', sarpanch_name: 'नमुना सरपंच',
    },
    applicant: { name: 'नमुना अर्जदार', address: 'नमुना संपूर्ण पत्ता' },
    subject: { name: 'नमुना लाभार्थी' },
    fields: {},
    officer: { name: 'नमुना अधिकारी' },
  };
  variables.forEach(variable => {
    if (!String(variable.path || '').startsWith('fields.')) return;
    const key = variable.path.replace(/^fields\./, '');
    if (variable.type === 'image' || variable.path.endsWith('_photo_url')) context.fields[key] = SAMPLE_PHOTO_DATA_URL;
    else if (variable.source !== 'form') return;
    else if (variable.type === 'date') context.fields[key] = '2026-08-03';
    else if (variable.type === 'number' || variable.type === 'calculated') context.fields[key] = '25';
    else if (variable.type === 'select') context.fields[key] = 'नमुना पर्याय';
    else context.fields[key] = variable.label || key.replace(/_/g, ' ');
  });
  return context;
};
